import { z } from 'zod';
import { Lang, Track } from './common.js';

export const SessionMode = z.enum(['self', 'guided']);
export type SessionMode = z.infer<typeof SessionMode>;

export const SessionStatus = z.enum([
  'invited', // link created, consent not yet given
  'in_progress', // consent given, answering
  'declined', // declined the cross-border consent; offered a call instead
  'followups', // I3 submitted; follow-ups generating or being answered
  'generating', // finish called; briefs being written
  'awaiting_review', // briefs ready; the review gate holds the client brief
  'complete', // client brief available to the client
  'failed', // brief generation failed after retries; consultant flagged
  'anonymised', // past retention: contact and free text dropped
]);
export type SessionStatus = z.infer<typeof SessionStatus>;

export const ConsentStep = z.enum(['processing', 'crossBorder']);
export type ConsentStep = z.infer<typeof ConsentStep>;

export const ConsentInput = z
  .object({
    step: ConsentStep,
    accepted: z.boolean(),
    /** The consent text version on screen, e.g. "v2-2026-11". */
    textVersion: z.string().min(2).max(40),
  })
  .strict();
export type ConsentInput = z.infer<typeof ConsentInput>;

export const SessionPatchInput = z
  .object({
    lang: Lang.optional(),
    /** Only while the session has no track yet: the welcome screen's 企业 AI 转型 / 出海增长 choice. */
    track: Track.optional(),
    currentQuestionId: z.string().max(10).optional(),
  })
  .strict();
export type SessionPatchInput = z.infer<typeof SessionPatchInput>;

export const FollowupType = z.enum(['single', 'multi', 'text']);

/** A follow-up as the client sees it: already in the session language. */
export const FollowupView = z.object({
  id: z.string().regex(/^FU\d$/),
  prompt: z.string(),
  type: FollowupType,
  options: z.array(z.object({ id: z.string(), label: z.string() })),
  allowOther: z.boolean(),
  answer: z
    .object({
      selected: z.array(z.string()),
      other: z.string().optional(),
      text: z.string().optional(),
      skipped: z.boolean().optional(),
    })
    .optional(),
});
export type FollowupView = z.infer<typeof FollowupView>;

/** Consultant view adds the one-line reason the model gave, and which trigger fired. */
export const FollowupRecord = FollowupView.extend({
  reason: z.string(),
  trigger: z.string().optional(),
  source: z.enum(['model', 'template']),
  answeredAt: z.string().optional(),
});
export type FollowupRecord = z.infer<typeof FollowupRecord>;

export const FollowupAnswerInput = z
  .object({
    selected: z.array(z.string().max(80)).max(10).default([]),
    other: z.string().max(200).optional(),
    text: z.string().max(2000).optional(),
    skipped: z.boolean().default(false),
  })
  .strict();
export type FollowupAnswerInput = z.infer<typeof FollowupAnswerInput>;

export const MemberCheckKey = z.enum(['pain', 'size', 'consequence', 'done', 'constraint', 'markets']);
export type MemberCheckKey = z.infer<typeof MemberCheckKey>;

/**
 * 我们听到的是: five lines templated from the answers named in the questionnaire's engine config
 * (consulting: C2, C3, C4, D2, G3; overseas: OE2, OE3, OE4, OF2, OC1); each confirmed with 对 / 不对 plus
 * an optional correction.
 */
export const MemberCheckLine = z.object({
  key: MemberCheckKey,
  source: z.string(),
  text: z.string(),
  confirmed: z.boolean().nullable(),
  correction: z.string().max(300).optional(),
});
export type MemberCheckLine = z.infer<typeof MemberCheckLine>;

export const ConfirmInput = z
  .object({
    lines: z
      .array(z.object({ key: MemberCheckKey, confirmed: z.boolean(), correction: z.string().max(300).optional() }))
      .max(5),
  })
  .strict();
export type ConfirmInput = z.infer<typeof ConfirmInput>;

export const DeleteRequestInput = z.object({ reason: z.string().max(500).optional() }).strict();

export const ConsentRecordView = z.object({
  acceptedAt: z.string().optional(),
  declinedAt: z.string().optional(),
  textVersion: z.string().optional(),
});
export type ConsentRecordView = z.infer<typeof ConsentRecordView>;
